import { WSList, WSSectionToolbar } from "@wingspanhq/fe-component-library";
import {
  IRequirementResponse,
  PayerPayeeStatus,
  RequirementStatus
} from "@wingspanhq/payments/dist/interfaces";
import React, { useMemo, useState } from "react";
import { ExtendedRequirementPreviewList } from "./ExtendedRequirementPreviewList";
import { ExtendedRequirementsTable } from "./ExtendedRequirementsTable";
import { RequirementContextType, RequirementTableItemData } from "../types";
import { RequirementsTable } from "./RequirementsTable";
import { RequirementsEmptyState } from "./RequirementsEmptyState";

export type PayerPayeeDetailsRequirementsProps = {
  noActions?: boolean;
  hasEligibleEngagements: boolean;
  requirements: IRequirementResponse[];
  archivedRequirements: IRequirementResponse[];
  contextType: RequirementContextType;
  goToRequirement: (requirement: IRequirementResponse) => void;
  status: PayerPayeeStatus;
};

export const PayerPayeeDetailsRequirements: React.FC<
  PayerPayeeDetailsRequirementsProps
> = ({
  hasEligibleEngagements,
  requirements,
  archivedRequirements,
  contextType,
  status,
  goToRequirement,
  noActions
}) => {
  const [showArchived, setShowArchived] = useState(false);

  const completedLength = useMemo(() => {
    return requirements.filter(e => e.status === RequirementStatus.Complete)
      .length;
  }, [requirements]);

  const isRequirementsComplete = useMemo(() => {
    return requirements.every(e => e.status === RequirementStatus.Complete);
  }, [requirements]);

  const [showPreviews, setShowPreviews] = useState(false);

  const getOnViewDetails = (requirement: RequirementTableItemData) => {
    return "requirementId" in requirement ? goToRequirement : undefined;
  };

  const isArchivalAvailable = archivedRequirements.length > 0;

  return (
    <WSList gap="L">
      <WSSectionToolbar
        title="Requirements"
        pill={
          status === PayerPayeeStatus.Inactive
            ? {
                icon: "archive",
                text: "Archived",
                theme: "neutral"
              }
            : requirements.length > 0
            ? {
                icon: true,
                text: `${completedLength} of ${requirements.length} completed`,
                theme:
                  isRequirementsComplete && hasEligibleEngagements
                    ? "success"
                    : "warning"
              }
            : undefined
        }
        buttons={[
          {
            label: "",
            onClick: () => setShowPreviews(true),
            kind: showPreviews ? "Secondary" : "Link",
            icon: "grid",
            onlyIcon: true,
            size: "S"
          },
          {
            label: "",
            onClick: () => setShowPreviews(false),
            kind: !showPreviews ? "Secondary" : "Link",
            icon: "list",
            onlyIcon: true,
            size: "S"
          }
        ]}
      />

      {requirements.length === 0 ? (
        <RequirementsEmptyState />
      ) : showPreviews ? (
        <ExtendedRequirementPreviewList
          requirements={requirements}
          contextType={contextType}
          getOnViewDetails={getOnViewDetails}
        />
      ) : (
        <ExtendedRequirementsTable
          requirements={requirements}
          contextType={contextType}
          hideActions={noActions}
          getOnViewDetails={getOnViewDetails}
          showEngagementsView={true}
        />
      )}

      {isArchivalAvailable ? (
        <>
          <WSSectionToolbar
            title={showArchived ? "Archived Requirements" : ""}
            buttons={[
              {
                label: `${showArchived ? "Hide" : "Show"} archived (${
                  archivedRequirements.length
                })`,
                kind: "Link",
                onClick: () => setShowArchived(showArchived => !showArchived)
              }
            ]}
          />
          {showArchived ? (
            <RequirementsTable
              requirements={archivedRequirements}
              contextType={contextType}
              onViewDetails={
                contextType === RequirementContextType.Payee
                  ? goToRequirement
                  : undefined
              }
            />
          ) : null}
        </>
      ) : null}
    </WSList>
  );
};
